/**
 * Chips de actividad de herramientas dentro de la burbuja del asistente:
 * «Consultando inventario…» girando → check verde al terminar. Es lo que
 * sustituye a la espera muda del asistente viejo — el tiempo real se va en
 * las consultas, y ahora se ve en qué.
 */

import { useTranslation } from "react-i18next";
import {
  Bug,
  CalendarDays,
  Check,
  CloudSun,
  Database,
  Euro,
  FileText,
  HelpCircle,
  Loader2,
  MapPin,
  Package,
  Users,
  X,
} from "lucide-react";
import type { ToolActivity } from "@/hooks/useAssistantStream";
import { cn } from "@/lib/utils";

const TOOL_ICONS: Record<string, typeof Package> = {
  get_inventory: Package,
  get_inventory_transactions: Package,
  get_scanned_invoices: FileText,
  search_phyto_registry: FileText,
  get_tasks: CalendarDays,
  get_cycles: CalendarDays,
  get_scouting_sessions: Bug,
  get_active_pests: Bug,
  get_pest_catalog: Bug,
  get_irm_status: Bug,
  get_costs: Euro,
  get_consumption_report: Euro,
  get_procurement: Euro,
  get_weather: CloudSun,
  get_structure: MapPin,
  get_phi_status: MapPin,
  get_team: Users,
  get_app_help: HelpCircle,
  get_spray_history: Database,
  resolve_entity: Database,
};

export function ToolActivityChips({ tools }: { tools: ToolActivity[] }) {
  const { t } = useTranslation();
  if (!tools.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {tools.map((tool) => {
        const Icon = TOOL_ICONS[tool.name] ?? Database;
        // El backend manda label ya localizada; si falta, clave dinámica con
        // fallback genérico (las claves dinámicas no cuentan para i18n:check).
        const label = tool.label
          || t(`ipmAssistant.tools.${tool.name}`, t("ipmAssistant.tools.generic", "Consultando datos…"));
        return (
          <span
            key={tool.id}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border bg-white py-1 ps-1 pe-2.5",
              // Sin tope de 220 px: cortaba etiquetas normales («Consultando el
              // registro fitosanitario…»). El tope es el ancho de la burbuja, y
              // como las pastillas van en una fila que envuelve, la que no cabe
              // baja a la línea siguiente en vez de estirar nada.
              "border-[#b6dded] max-w-full min-w-0",
            )}
          >
            <span className="w-4 h-4 rounded-[5px] bg-[#2E84DE] flex items-center justify-center shrink-0">
              <Icon className="w-2.5 h-2.5 text-white" />
            </span>
            <span className="text-[11px] font-medium text-[#2E4A63] min-w-0 break-words">
              {label}
            </span>
            {tool.status === "running" ? (
              <Loader2 className="w-3 h-3 shrink-0 animate-spin text-[#2E84DE]" />
            ) : tool.status === "error" ? (
              <X className="w-3 h-3 shrink-0 text-destructive" />
            ) : (
              <Check className="w-3 h-3 shrink-0 text-emerald-600" />
            )}
          </span>
        );
      })}
    </div>
  );
}
